import { useCallback, useEffect, useState } from 'react'
import { BUILD_VERSION } from '../generated/version'
import './VersionUpdateNotice.css'

type ChangelogHead = { currentVersion?: string }
const displayVersion = (version: string) => version.split('-')[0]

const POLL_INTERVAL_MS = 5 * 60 * 1000

async function clearFrontendCaches(): Promise<void> {
  if ('serviceWorker' in navigator) {
    const registrations = await navigator.serviceWorker.getRegistrations()
    await Promise.all(registrations.map((registration) => registration.unregister()))
  }
  if ('caches' in window) {
    const keys = await caches.keys()
    await Promise.all(keys.map((key) => caches.delete(key)))
  }
}

export default function VersionUpdateNotice({ embedded }: { embedded?: boolean }) {
  const [latestVersion, setLatestVersion] = useState<string | null>(null)
  const isEmbedded = embedded ?? (typeof window !== 'undefined' && window.self !== window.top)

  const check = useCallback(async () => {
    try {
      // In Module Federation, window.location belongs to Lens. Resolve from this
      // remote chunk so the check reaches the plugin deployment, not the host.
      const target = import.meta.env.DEV
        ? new URL(`${import.meta.env.BASE_URL || '/'}changelog.json`, window.location.origin)
        : new URL('../changelog.json', import.meta.url)
      target.searchParams.set('version_check', String(Date.now()))
      const response = await fetch(target.toString(), {
        cache: 'no-store',
        headers: { Accept: 'application/json' },
      })
      if (!response.ok) return
      const data = await response.json() as ChangelogHead
      if (data.currentVersion && data.currentVersion !== BUILD_VERSION) {
        setLatestVersion(data.currentVersion)
      } else {
        setLatestVersion(null)
      }
    } catch {
      // A failed availability check must not interrupt the application.
    }
  }, [])

  useEffect(() => {
    void check()
    const timer = window.setInterval(() => void check(), POLL_INTERVAL_MS)
    const onVisibility = () => { if (document.visibilityState === 'visible') void check() }
    window.addEventListener('focus', check)
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      window.clearInterval(timer)
      window.removeEventListener('focus', check)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [check])

  if (!latestVersion) return null

  const updateStandalone = async () => {
    try { await clearFrontendCaches() } finally {
      const url = new URL(window.location.href)
      url.searchParams.set('__version_reload', latestVersion)
      window.location.replace(url.toString())
    }
  }

  return (
    <aside className="vun-notice" role="status" aria-live="polite">
      <div className="vun-copy">
        <strong>Доступна новая версия {displayVersion(latestVersion)}</strong>
        <span>Сейчас открыта устаревшая версия {displayVersion(BUILD_VERSION)}.</span>
      </div>
      {isEmbedded ? (
        <button type="button" onClick={() => window.location.reload()}>Переоткрыть плагин</button>
      ) : (
        <button type="button" onClick={() => void updateStandalone()}>Обновить сейчас</button>
      )}
    </aside>
  )
}
